import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ExpansionPanel, InputField } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { CompanyAnnual } from '@/services/accountService/companyAnnualService';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  disableAccordion?: boolean;
  initialData?: { referrer: string | null; inCharge: string | null } | null;
  externalErrors?: {
    referrer?: boolean;
    inCharge?: boolean;
  };
};

export default function EpOther({ annualId, onChange, disableAccordion = false, initialData, externalErrors }: Props) {
  const getOtherInfo = useCompanyAnnualStore(s => s.getOtherInfo);
  const storeAnnual = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);
  const [local, setLocal] = useState<{ referrer: string | null; inCharge: string | null }>({ referrer: null, inCharge: null });

  useEffect(() => {
    // 如果有 initialData，优先使用
    if (initialData) {
      setLocal(initialData);
      return;
    }
    
    let mounted = true;
    (async () => {
      const v = await getOtherInfo(annualId);
      if (!mounted) return;
      setLocal(v);
    })();
    return () => { mounted = false; };
  }, [annualId, getOtherInfo, initialData]);

  // Reactively sync from store when data changes (e.g. from Content Sheet import)
  useEffect(() => {
    if (!storeAnnual) return;
    setLocal(prev => {
      const nextRef = storeAnnual.referrer ?? prev.referrer;
      const nextIC = storeAnnual.inCharge ?? prev.inCharge;
      if (nextRef === prev.referrer && nextIC === prev.inCharge) return prev;
      return { referrer: nextRef, inCharge: nextIC };
    });
  }, [storeAnnual]);

  const emit = (patch: Partial<typeof local>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(patch as Partial<CompanyAnnual>);
  };

  return (
    <ExpansionPanel 
      title="Other (其他)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <Grid container spacing={0.5}>
        <Grid size={6 as any}>
          <InputField 
            fullWidth 
            variant="outlined" 
            label="Referrer (審計工作介紹人)" 
            value={local.referrer || ''} 
            onChange={(e) => emit({ referrer: e.target.value || null })}
            error={externalErrors?.referrer}
            helperText={externalErrors?.referrer ? 'Referrer is required' : undefined}
          />
        </Grid>
        <Grid size={6 as any}>
          <InputField 
            fullWidth 
            variant="outlined" 
            label="In-Charge (審計工作負責人)" 
            value={local.inCharge || ''} 
            onChange={(e) => emit({ inCharge: e.target.value || null })}
            error={externalErrors?.inCharge}
            helperText={externalErrors?.inCharge ? 'In-Charge is required' : undefined}
          />
        </Grid>
      </Grid>
    </ExpansionPanel>
  );
}
